import {
  AccountUpdateInput,
  BillingAccount,
  BillingContact,
  Enforcement,
  TenantBilling,
} from "api/billing";
import { TenantLinkStatus } from "api/teleopdLink";

// Rules tab form model + validation mirroring validations/billingValidation.js.

export interface AudienceRole {
  id: string;
  name: string;
}

export interface ContactDraft {
  key: string;
  name: string;
  email: string;
  /** WhatsApp number as typed; saved as +91XXXXXXXXXX. */
  phone: string;
}

export interface RulesForm {
  enforcement: Enforcement;
  warningDays: string;
  graceDays: string;
  audience: AudienceRole[];
  remindersEnabled: boolean;
  offsets: number[];
  contacts: ContactDraft[];
  prefixesText: string;
}

export interface RulesErrors {
  warningDays: string | null;
  graceDays: string | null;
  contacts: Record<string, string>;
  contactsList: string | null;
  prefixes: string[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Characters that would mean something in a regex; TeleOpd matches prefixes with startsWith only.
const PREFIX_FORBIDDEN = /[\s^$*+?()[\]{}|\\]/;
const READ_ONLY_SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;
export const MAX_CONTACTS = 10;
export const MAX_PREFIXES = 20;
export const MAX_OFFSETS = 10;

let contactSeed = 0;
export const newContact = (name = "", email = "", phone = ""): ContactDraft => {
  contactSeed += 1;
  return { key: `contact-${contactSeed}`, name, email, phone };
};

// Same rule as the server (utils/phone.js formatIndianPhone): 10 digits, +91 optional.
export const indianMobile = (value: string): string | null => {
  const digits = value.replace(/\D/g, "");
  const local = digits.length !== 10 && digits.startsWith("91") ? digits.slice(2) : digits;
  const lastTen = local.slice(-10);
  return lastTen.length === 10 ? `+91${lastTen}` : null;
};

export type SavedRules = Pick<BillingAccount, "contacts" | "policy" | "reminders">;

export const formFromAccount = (account: SavedRules): RulesForm => ({
  enforcement: account.policy.enforcement,
  warningDays: String(account.policy.warningDays),
  graceDays: String(account.policy.graceDays),
  audience: account.policy.audienceRoleIds.map((id, index) => ({
    id,
    name: account.policy.audienceRoleNames[index] || id,
  })),
  remindersEnabled: account.reminders.enabled,
  offsets: account.reminders.offsetsDays.slice().sort((a, b) => a - b),
  contacts: account.contacts.map((contact) =>
    newContact(contact.name, contact.email || "", contact.phone || "")
  ),
  prefixesText: account.policy.extraWritablePrefixes.join("\n"),
});

const prefixLines = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim().toLowerCase())
    .filter(Boolean);

const cleanContacts = (contacts: ContactDraft[]) =>
  contacts
    .filter((contact) => contact.name.trim() || contact.email.trim() || contact.phone.trim())
    .map((contact) => ({
      name: contact.name.trim(),
      email: contact.email.trim().toLowerCase() || null,
      phone: contact.phone.trim() ? indianMobile(contact.phone) || contact.phone.trim() : null,
    }));

const isIntInRange = (value: string, min: number, max: number) =>
  /^\d+$/.test(value.trim()) && Number(value) >= min && Number(value) <= max;

export const buildAccountUpdate = (form: RulesForm): AccountUpdateInput => ({
  contacts: cleanContacts(form.contacts),
  policy: {
    enforcement: form.enforcement,
    warningDays: Number(form.warningDays.trim()),
    graceDays: Number(form.graceDays.trim()),
    audienceRoleIds: form.audience.map((role) => role.id),
    audienceRoleNames: form.audience.map((role) => role.name),
    extraWritablePrefixes: prefixLines(form.prefixesText),
  },
  reminders: {
    enabled: form.remindersEnabled,
    offsetsDays: form.offsets.slice().sort((a, b) => a - b),
  },
});

const ENFORCEMENT_RANK: Record<Enforcement, number> = { off: 0, warn_only: 1, read_only: 2 };

/**
 * Changes that weaken enforcement (lower mode, more grace days, new writable paths). The API
 * accepts them only with billing/approve and a reason, like pausing enforcement.
 */
export const relaxedRules = (saved: RulesForm, form: RulesForm): string[] => {
  const relaxed: string[] = [];
  if (ENFORCEMENT_RANK[form.enforcement] < ENFORCEMENT_RANK[saved.enforcement]) {
    relaxed.push("enforcement");
  }
  if (Number(form.graceDays) > Number(saved.graceDays)) {
    relaxed.push("grace days");
  }
  const savedPrefixes = prefixLines(saved.prefixesText);
  if (prefixLines(form.prefixesText).some((prefix) => !savedPrefixes.includes(prefix))) {
    relaxed.push("writable paths");
  }
  return relaxed;
};

/** Stable comparison key for unsaved-change detection. */
export const formFingerprint = (form: RulesForm): string =>
  JSON.stringify({
    ...buildAccountUpdate(form),
    warningDays: form.warningDays.trim(),
    graceDays: form.graceDays.trim(),
  });

export const validateRules = (form: RulesForm): RulesErrors => {
  const contacts: Record<string, string> = {};
  form.contacts.forEach((contact) => {
    const name = contact.name.trim();
    const email = contact.email.trim();
    const phone = contact.phone.trim();
    if (!name && !email && !phone) {
      return;
    }
    if (!email && !phone) {
      contacts[contact.key] = "Add an email address or a WhatsApp number.";
    } else if (email && (!EMAIL_PATTERN.test(email) || email.length > 254)) {
      contacts[contact.key] = "That email address doesn't look right.";
    } else if (phone && !indianMobile(phone)) {
      contacts[contact.key] = "Use a 10-digit mobile number (+91 optional).";
    } else if (name.length > 120) {
      contacts[contact.key] = "Keep the name under 120 characters.";
    }
  });
  const cleaned = cleanContacts(form.contacts);
  const addresses = cleaned.reduce<string[]>(
    (all, contact) =>
      all.concat([contact.email, contact.phone].filter((value): value is string => Boolean(value))),
    []
  );
  const duplicate = addresses.find((address, index) => addresses.indexOf(address) !== index);

  const prefixes: string[] = [];
  const lines = prefixLines(form.prefixesText);
  if (lines.length > MAX_PREFIXES) {
    prefixes.push(`Use at most ${MAX_PREFIXES} prefixes.`);
  }
  lines.forEach((line) => {
    if (!line.startsWith("/")) {
      prefixes.push(`“${line}” must start with /.`);
    } else if (line.length < 2 || line.length > 200) {
      prefixes.push(`“${line}” must be 2 to 200 characters.`);
    } else if (PREFIX_FORBIDDEN.test(line)) {
      prefixes.push(`“${line}” must be a plain path: no spaces or regex characters.`);
    }
  });

  return {
    warningDays: isIntInRange(form.warningDays, 3, 60) ? null : "Use a whole number from 3 to 60.",
    graceDays: isIntInRange(form.graceDays, 0, 60) ? null : "Use a whole number from 0 to 60.",
    contacts,
    contactsList:
      cleaned.length > MAX_CONTACTS
        ? `Use at most ${MAX_CONTACTS} contacts.`
        : duplicate
        ? `${duplicate} is listed twice.`
        : null,
    prefixes,
  };
};

export const hasRuleErrors = (errors: RulesErrors) =>
  Boolean(
    errors.warningDays ||
      errors.graceDays ||
      errors.contactsList ||
      Object.keys(errors.contacts).length > 0 ||
      errors.prefixes.length > 0
  );

/** Why "read-only" can't be chosen right now (empty when TeleOpd is capable). */
export const readOnlyBlockers = (
  link: TenantLinkStatus | undefined,
  now = Date.now()
): string[] => {
  if (!link) {
    return ["Checking the TeleOpd connection…"];
  }
  if (link.readOnlyCapable) {
    return [];
  }
  if (!link.linked) {
    return ["TeleOpd is not connected yet (Connection tab)."];
  }
  const reasons: string[] = [];
  if (!link.enabled) {
    reasons.push("The TeleOpd link is disabled.");
  }
  if (link.lockSwitch !== "on") {
    reasons.push(
      `TeleOpd's lock switch is “${
        link.lockSwitch || "unknown"
      }” (must be “on”: set SAP_LOCK=on on the deployment and restart it).`
    );
  }
  const lastSync = link.lastSyncAt ? new Date(link.lastSyncAt).getTime() : NaN;
  if (!Number.isFinite(lastSync) || now - lastSync > READ_ONLY_SYNC_WINDOW_MS) {
    reasons.push("No sync from TeleOpd in the last 24 hours.");
  }
  if (reasons.length === 0) {
    reasons.push("TeleOpd hasn't reported its read-only guard (readonly_guard_v1) yet.");
  }
  return reasons;
};

type Notifications = TenantBilling["notifications"];

/** Who a reminder would reach now: emails while email is on, WhatsApp numbers while WhatsApp is on. */
export const reminderRecipients = (
  contacts: BillingContact[],
  notifications: Notifications
): string[] =>
  [
    ...(notifications.email ? contacts.map((contact) => contact.email) : []),
    ...(notifications.whatsappReminders ? contacts.map((contact) => contact.phone) : []),
  ].filter((address): address is string => Boolean(address));

/** "WhatsApp and email", "WhatsApp", "email" or null when no reminder channel is set up. */
export const reminderChannelsLabel = (notifications: Notifications): string | null => {
  const names = [
    notifications.whatsappReminders ? "WhatsApp" : null,
    notifications.email ? "email" : null,
  ].filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(" and ") : null;
};
